/* eslint-disable max-lines */
import type { EntityModifyParameters, ExtendedEntityParameters } from 'src/components/EntityDetail/entity-route-util';
import { type ValidationResult } from 'types';
import { GetWorkInstructionVersionDocument, InsertWorkInstructionDocument, InsertWorkInstructionVersionDocument, UpdatePendingWorkInstructionVersionDocument } from 'types/db';
import { ArgoWorkInstructionVersionStatusEnum } from '@redviking/argonaut-util/types/db-enums/argo/work_instruction_version_status';
import type { Route } from 'vue-router';
import {
    type WorkInstructionStep,
    type WorkInstructionVersion,
    workInstructionVersionSchema,
} from '@redviking/argonaut-util/types/work-instruction-version';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { EntityDetailMode } from '@redviking/argonaut-core-ui/src/components/EntityDetail/mode';
import { Notify } from 'src/notifications';
import { v4 as uuidV4 } from 'uuid';
import { fromZodError } from 'zod-validation-error';
import { CausalError } from '@redviking/causal-error';
import { validateWorkInstruction } from './work-instruction.validation';

export type WorkInstructionEntity = {
    id: string;
    name: string;
    version: {
        id: string;
        number: number | null;
        config: WorkInstructionVersion;
        status: ArgoWorkInstructionVersionStatusEnum;
    };
};

export const createWorkInstruction = async (payload: EntityModifyParameters<'workInstruction'>): Promise<ValidationResult> => {
    const { entity } = payload;

    const validationResult = validateWorkInstruction(entity);

    if (validationResult.status !== 'success') {
        return validationResult;
    }

    const {
        insert_argo_work_instruction_one,
    } = await gqlClient.request({
        document: InsertWorkInstructionDocument,
        variables: {
            workInstruction: {
                name: entity.name,
            },
        },
    });
    if (!insert_argo_work_instruction_one) {
        return {
            status: 'error',
            errorMessage: 'Work Instruction creation did not return any data',
        };
    }

    const {
       insert_argo_work_instruction_version_one,
    } = await gqlClient.request({
        document: InsertWorkInstructionVersionDocument,
        variables: {
            workInstructionVersion: {
                config: entity.version.config,
                work_instruction_id: insert_argo_work_instruction_one.id,
            },
        },
    });

    if (!insert_argo_work_instruction_version_one) {
        return {
            status: 'error',
            errorMessage: 'Work Instruction Version creation did not return any data',
        };
    }

    const configParseResult = workInstructionVersionSchema.safeParse(insert_argo_work_instruction_version_one.config);
    if (!configParseResult.success) {
        const zodError = fromZodError(configParseResult.error);
        return {
            status: 'error',
            errorMessage: `Encountered an error while attempting to parse the work instruction config: ${zodError.message}`,
        };
    }

    return {
        status: 'success',
        newRoute: {
            name: 'workInstructionSettings',
            params: {
                workInstructionId: insert_argo_work_instruction_one.id,
                versionId: insert_argo_work_instruction_version_one.id,
            },
        },
    };
};

export const saveWorkInstruction = async (payload: ExtendedEntityParameters<'workInstruction'>): Promise<ValidationResult> => {
    const {
        mode,
        entity,
    } = payload;

    const validationResult = validateWorkInstruction(entity);

    if (validationResult.status !== 'success') {
        return validationResult;
    }

    if (mode === EntityDetailMode.Fork) {
        const {
            insert_argo_work_instruction_version_one,
        } = await gqlClient.request({
            document: InsertWorkInstructionVersionDocument,
            variables: {
                workInstructionVersion: {
                    config: entity.version.config,
                    work_instruction_id: entity.id,
                },
            },
        });

        if (!insert_argo_work_instruction_version_one) {
            return {
                status: 'error',
                errorMessage: 'Work Instruction Version fork did not return any data',
            };
        }

        const configParseResult = workInstructionVersionSchema.safeParse(insert_argo_work_instruction_version_one.config);
        if (!configParseResult.success) {
            const zodError = fromZodError(configParseResult.error);
            return {
                status: 'error',
                errorMessage: `Encountered an error while attempting to parse the work instruction config: ${zodError.message}`,
            };
        }

        return {
            status: 'success',
            newRoute: {
                name: 'workInstructionSettings',
                params: {
                    workInstructionId: entity.id,
                    versionId: insert_argo_work_instruction_version_one.id,
                },
            },
        };
    }

    const {
        update_argo_work_instruction_version_by_pk,
    } = await gqlClient.request({
        document: UpdatePendingWorkInstructionVersionDocument,
        variables: {
            workInstructionId: entity.id,
            name: entity.name,
            workInstructionVersionId: entity.version.id,
            workInstructionVersionSetInput: {
                config: entity.version.config,
            },
        },
    });

    if (!update_argo_work_instruction_version_by_pk) {
        return {
            status: 'error',
            errorMessage: 'Work Instruction Version update did not return any data',
        };
    }

    const configParseResult = workInstructionVersionSchema.safeParse(update_argo_work_instruction_version_by_pk.config);
    if (!configParseResult.success) {
        const zodError = fromZodError(configParseResult.error);
        return {
            status: 'error',
            errorMessage: `Encountered an error while attempting to parse the work instruction config: ${zodError.message}`,
        };
    }

    return {
        status: 'success',
    };
};

const getWorkInstructionStep = (): WorkInstructionStep => ({
    outputs: {},
    stepNum: 0,
    id: uuidV4(),
    name: 'Step 1',
    annotations: [],
    linkedMedia: null,
    linkedProcessIds: [],
    workInstruction: 'Step 1',
    advanceConfig: {
        kind: 'all',
        conditions: [],
    },
});

export const getWorkInstruction = async (to: Route): Promise<{ entity: WorkInstructionEntity }> => {
    if (to.query.mode === 'create') {
        const entityData: WorkInstructionEntity = {
            id: '',
            name: '',
            version: {
                id: '',
                // pending work instructions do not have a version number
                number: null,
                status: ArgoWorkInstructionVersionStatusEnum.Staged,
                config: {
                    outputs: {},
                    steps: [
                        getWorkInstructionStep(),
                    ],
                },
            },
        };
        return Promise.resolve({
            entity: entityData,
        });
    }
    const {
       argo_work_instruction_version_by_pk,
    } = await gqlClient.request({
        document: GetWorkInstructionVersionDocument,
        variables: {
            workInstructionVersionId: to.params.versionId,
        },
    });

    if (!argo_work_instruction_version_by_pk) {
        throw new Error('Work Instruction Version not found');
    }

    const configParseResult = workInstructionVersionSchema.safeParse(argo_work_instruction_version_by_pk.config);
    if (!configParseResult.success) {
        const zodError = fromZodError(configParseResult.error);
        const err = CausalError.from({
            message: 'Encountered an error while attempting to parse the config',
            cause: zodError.message, // including the whole zod error causes too much noise, so we'll include it alongside it
            zodError,
        });
        Notify.error(err);
        throw err;
    }

    if (to.query.mode === 'fork') {
        // Output ids are referenced externally (e.g. by Applet flow "Work Instruction" nodes configured at design time),
        // so outputs that are carried over unchanged from the forked version must keep their original ids. Only outputs
        // added after the fork (via "Add An Output") get a newly generated id.
        const forkedEntity: WorkInstructionEntity = {
            id: argo_work_instruction_version_by_pk.work_instruction.id,
            name: argo_work_instruction_version_by_pk.work_instruction.name,
            version: {
                id: 'new', // note: this does not get passed to the db we let the db generate the id
                number: null,
                status: ArgoWorkInstructionVersionStatusEnum.Staged,
                config: {
                    outputs: configParseResult.data.outputs,
                    steps: configParseResult.data.steps.map(s => ({
                        ...s,
                        id: uuidV4(),
                        outputs: s.outputs,
                    })),
                },
            },
        };

        return {
            entity: forkedEntity,
        };
    }

    const entity: WorkInstructionEntity = {
        id: argo_work_instruction_version_by_pk.work_instruction.id,
        name: argo_work_instruction_version_by_pk.work_instruction.name,
        version: {
            number: null,
            id: argo_work_instruction_version_by_pk.id,
            status: argo_work_instruction_version_by_pk.status,
            config: configParseResult.data,
        },
    };

    return {
        entity,
    };
};
